import { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react';
import type {
  Distance,
  RunnerLevel,
  UserProfile,
  UserProfileInput,
} from '@runner/shared';
import { useAuth } from '../auth/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { apiFetch } from '../api/fetch';
import { getAllRegions } from '../utils/regionMapper';
import {
  parseFinishTime,
  formatFinishTime,
  toPacePerKm,
} from '../utils/finishTime';
import './ProfilePage.css';

type PaceKey = 'pace5kSec' | 'pace10kSec' | 'paceHalfSec' | 'paceFullSec';

interface PaceFieldDef {
  key: PaceKey;
  label: string;
  distanceKm: number;
  /** Min/max from UserProfileSchema (packages/shared). */
  min: number;
  max: number;
}

const PACE_FIELDS: PaceFieldDef[] = [
  { key: 'pace5kSec', label: '5K', distanceKm: 5, min: 600, max: 3600 },
  { key: 'pace10kSec', label: '10K', distanceKm: 10, min: 1200, max: 7200 },
  { key: 'paceHalfSec', label: 'Half', distanceKm: 21.0975, min: 2400, max: 14400 },
  { key: 'paceFullSec', label: 'Full', distanceKm: 42.195, min: 5400, max: 28800 },
];

const LEVELS: { value: RunnerLevel; label: string; hint: string }[] = [
  { value: 'beginner', label: '입문', hint: '5K 페이스 6:30/km 이상' },
  { value: 'intermediate', label: '중급', hint: '5K 페이스 5:00~6:30/km' },
  { value: 'advanced', label: '상급', hint: '5K 페이스 4:00~5:00/km' },
  { value: 'elite', label: '엘리트', hint: '5K 페이스 4:00/km 미만' },
];

const DISTANCES: { value: Distance; label: string }[] = [
  { value: '5K', label: '5K' },
  { value: '10K', label: '10K' },
  { value: 'half', label: 'Half' },
  { value: 'full', label: 'Full' },
  { value: 'ultra', label: 'Ultra' },
  { value: 'other', label: '기타' },
];

const BIO_MAX = 500;

interface FormState {
  level: RunnerLevel | null;
  paceInputs: Record<PaceKey, string>;
  preferredDistances: Distance[];
  preferredRegions: string[];
  bio: string;
}

function profileToForm(profile: UserProfile | null): FormState {
  const paceInputs = {
    pace5kSec: '',
    pace10kSec: '',
    paceHalfSec: '',
    paceFullSec: '',
  } as Record<PaceKey, string>;
  for (const f of PACE_FIELDS) {
    const sec = profile?.[f.key] ?? null;
    paceInputs[f.key] = sec !== null && sec !== undefined ? formatFinishTime(sec) : '';
  }
  return {
    level: profile?.level ?? null,
    paceInputs,
    preferredDistances: profile?.preferredDistances ?? [],
    preferredRegions: profile?.preferredRegions ?? [],
    bio: profile?.bio ?? '',
  };
}

/**
 * Parse pace inputs into { value, error } per field. Empty inputs → null (cleared).
 * Non-empty inputs that fail to parse or fall outside the schema range are treated
 * as blocking errors and prevent save.
 */
function resolvePaceInputs(
  inputs: Record<PaceKey, string>,
): { values: Record<PaceKey, number | null>; errors: Partial<Record<PaceKey, string>> } {
  const values = {} as Record<PaceKey, number | null>;
  const errors: Partial<Record<PaceKey, string>> = {};
  for (const f of PACE_FIELDS) {
    const raw = inputs[f.key].trim();
    if (raw === '') {
      values[f.key] = null;
      continue;
    }
    const sec = parseFinishTime(raw);
    if (sec === null) {
      errors[f.key] = 'MM:SS 또는 H:MM:SS 형식으로 입력하세요';
      values[f.key] = null;
      continue;
    }
    if (sec < f.min || sec > f.max) {
      errors[f.key] = `${formatFinishTime(f.min)}~${formatFinishTime(f.max)} 사이 값이어야 합니다`;
      values[f.key] = null;
      continue;
    }
    values[f.key] = sec;
  }
  return { values, errors };
}

function diffInput(
  form: FormState,
  paceValues: Record<PaceKey, number | null>,
  baseline: UserProfile | null,
): UserProfileInput {
  const out: UserProfileInput = {};
  const baseLevel = baseline?.level ?? null;
  if (form.level !== baseLevel) out.level = form.level;

  for (const f of PACE_FIELDS) {
    const base = baseline?.[f.key] ?? null;
    if (paceValues[f.key] !== base) {
      out[f.key] = paceValues[f.key];
    }
  }

  const baseDistances = baseline?.preferredDistances ?? [];
  if (!arrayEquals(form.preferredDistances, baseDistances)) {
    out.preferredDistances = form.preferredDistances;
  }

  const baseRegions = baseline?.preferredRegions ?? [];
  if (!arrayEquals(form.preferredRegions, baseRegions)) {
    out.preferredRegions = form.preferredRegions;
  }

  const baseBio = baseline?.bio ?? '';
  if (form.bio.trim() !== baseBio) {
    out.bio = form.bio.trim() === '' ? null : form.bio.trim();
  }

  return out;
}

function arrayEquals<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function toggleIn<T>(arr: readonly T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

export function ProfilePage() {
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const { profile, loading, saving, save } = useProfile();
  const [form, setForm] = useState<FormState>(() => profileToForm(null));
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const regions = useMemo(() => getAllRegions(), []);
  const formIdPrefix = useId();

  useEffect(() => {
    setForm(profileToForm(profile));
  }, [profile]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const { values: paceValues, errors: paceErrors } = useMemo(
    () => resolvePaceInputs(form.paceInputs),
    [form.paceInputs],
  );
  const hasPaceErrors = Object.keys(paceErrors).length > 0;

  const pendingInput = useMemo(
    () => diffInput(form, paceValues, profile),
    [form, paceValues, profile],
  );
  const hasChanges = Object.keys(pendingInput).length > 0;
  const canSave = hasChanges && !hasPaceErrors && !saving;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    try {
      await save(pendingInput);
      setToast({ kind: 'success', text: '저장되었습니다' });
    } catch (e) {
      setToast({
        kind: 'error',
        text: e instanceof Error ? e.message : '저장에 실패했습니다',
      });
    }
  }, [canSave, pendingInput, save]);

  const handleAvatarUpload = useCallback(
    async (file: File) => {
      setAvatarBusy(true);
      setAvatarError(null);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await apiFetch('/api/users/me/avatar', { method: 'POST', body: fd });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            error?: { message?: string };
          };
          throw new Error(body.error?.message ?? `HTTP ${res.status}`);
        }
        await refreshAuth();
        setToast({ kind: 'success', text: '아바타가 업데이트되었습니다' });
      } catch (e) {
        setAvatarError(e instanceof Error ? e.message : '업로드 실패');
      } finally {
        setAvatarBusy(false);
      }
    },
    [refreshAuth],
  );

  const handleAvatarReset = useCallback(async () => {
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      const res = await apiFetch('/api/users/me/avatar', { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        throw new Error(`HTTP ${res.status}`);
      }
      await refreshAuth();
      setToast({ kind: 'success', text: '기본 아바타로 복구했습니다' });
    } catch (e) {
      setAvatarError(e instanceof Error ? e.message : '아바타 초기화 실패');
    } finally {
      setAvatarBusy(false);
    }
  }, [refreshAuth]);

  const updatePace = (key: PaceKey, value: string) =>
    setForm((prev) => ({
      ...prev,
      paceInputs: { ...prev.paceInputs, [key]: value },
    }));

  // 인증(/me) 로딩 중에는 useProfile 이 userId=null 이라 loading=false·profile=null 로
  // 일찍 풀린다. 이때 폼을 렌더하면, 뒤늦게 도착한 user→profile refetch 의 동기화 effect가
  // 사용자가 막 입력한 값을 덮어쓴다(테스트 flaky 의 원인). 인증 로딩도 가드에 포함해
  // 폼 입력 가능 시점을 데이터 준비 이후로 고정한다.
  if ((loading || authLoading) && !profile) {
    return <div className="profile-page"><div className="profile-loading">불러오는 중…</div></div>;
  }

  return (
    <div className="profile-page">
      <header className="profile-header">
        <div>
          <h1 className="profile-title">프로필</h1>
          <p className="profile-subtitle">내 러닝 정보를 설정하면 맞춤 대회 추천을 받을 수 있어요.</p>
        </div>
        <div className="profile-save-bar">
          <SaveButton
            saving={saving}
            hasChanges={hasChanges}
            hasErrors={hasPaceErrors}
            onClick={handleSave}
            testId="save-top"
          />
        </div>
      </header>

      {toast && (
        <div
          className={`profile-toast profile-toast-${toast.kind}`}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          data-testid="profile-toast"
        >
          {toast.text}
        </div>
      )}

      <section className="profile-section" aria-labelledby={`${formIdPrefix}-basic`}>
        <h2 id={`${formIdPrefix}-basic`} className="profile-section-title">기본 정보</h2>
        <div className="profile-basic-grid">
          <div className="profile-avatar-box">
            {user?.avatarUrl ? (
              <img
                className="profile-avatar-img"
                src={user.avatarUrl}
                alt=""
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="profile-avatar-fallback" aria-hidden="true">
                {user?.nickname.slice(0, 1) ?? '?'}
              </div>
            )}
            <div className="profile-avatar-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="profile-avatar-file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleAvatarUpload(file);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                className="profile-btn profile-btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarBusy}
                aria-busy={avatarBusy}
              >
                {avatarBusy ? '업로드 중…' : '아바타 변경'}
              </button>
              <button
                type="button"
                className="profile-btn profile-btn-link"
                onClick={handleAvatarReset}
                disabled={avatarBusy}
              >
                기본으로 복구
              </button>
            </div>
            {avatarError && (
              <div className="profile-field-error" role="alert">
                {avatarError}
              </div>
            )}
          </div>

          <div className="profile-basic-fields">
            <div className="profile-field">
              <label className="profile-field-label" htmlFor={`${formIdPrefix}-nickname`}>
                닉네임
              </label>
              <input
                id={`${formIdPrefix}-nickname`}
                className="profile-input"
                type="text"
                value={user?.nickname ?? ''}
                readOnly
              />
              <p className="profile-field-help">닉네임 변경은 추후 지원됩니다.</p>
            </div>
            <div className="profile-field">
              <label className="profile-field-label" htmlFor={`${formIdPrefix}-email`}>
                이메일
              </label>
              <input
                id={`${formIdPrefix}-email`}
                className="profile-input"
                type="email"
                value={user?.email ?? ''}
                readOnly
              />
            </div>
          </div>
        </div>
      </section>

      <section className="profile-section" aria-labelledby={`${formIdPrefix}-level`}>
        <h2 id={`${formIdPrefix}-level`} className="profile-section-title">러너 레벨</h2>
        <div className="profile-level-grid" role="radiogroup" aria-labelledby={`${formIdPrefix}-level`}>
          {LEVELS.map((lvl) => {
            const id = `${formIdPrefix}-level-${lvl.value}`;
            const checked = form.level === lvl.value;
            return (
              <label key={lvl.value} className={`profile-level-option ${checked ? 'is-checked' : ''}`} htmlFor={id}>
                <input
                  id={id}
                  type="radio"
                  name={`${formIdPrefix}-level`}
                  value={lvl.value}
                  checked={checked}
                  onChange={() => setForm((p) => ({ ...p, level: lvl.value }))}
                />
                <span className="profile-level-label">{lvl.label}</span>
                <span className="profile-level-hint">{lvl.hint}</span>
              </label>
            );
          })}
        </div>
      </section>

      <section className="profile-section" aria-labelledby={`${formIdPrefix}-pace`}>
        <h2 id={`${formIdPrefix}-pace`} className="profile-section-title">페이스 기록</h2>
        <p className="profile-section-desc">완주 시간을 MM:SS 또는 H:MM:SS 형식으로 입력하세요. 모르는 기록은 비워둬도 됩니다.</p>
        <div className="profile-pace-grid">
          {PACE_FIELDS.map((f) => {
            const id = `${formIdPrefix}-${f.key}`;
            const errorId = `${id}-error`;
            const hintId = `${id}-hint`;
            const raw = form.paceInputs[f.key];
            const err = paceErrors[f.key];
            const sec = paceValues[f.key];
            const pacePerKm = sec !== null && sec > 0 ? toPacePerKm(sec, f.distanceKm) : '';
            return (
              <div key={f.key} className="profile-field">
                <label className="profile-field-label" htmlFor={id}>
                  {f.label} 완주 시간
                </label>
                <input
                  id={id}
                  className={`profile-input ${err ? 'is-invalid' : ''}`}
                  type="text"
                  inputMode="numeric"
                  placeholder={f.key === 'paceFullSec' || f.key === 'paceHalfSec' ? '1:30:00' : '22:30'}
                  value={raw}
                  onChange={(e) => updatePace(f.key, e.target.value)}
                  aria-invalid={err ? true : undefined}
                  aria-describedby={err ? errorId : pacePerKm ? hintId : undefined}
                />
                {err ? (
                  <div id={errorId} className="profile-field-error" role="alert">{err}</div>
                ) : pacePerKm ? (
                  <div id={hintId} className="profile-field-help">≈ {pacePerKm}</div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      <section className="profile-section" aria-labelledby={`${formIdPrefix}-dist`}>
        <h2 id={`${formIdPrefix}-dist`} className="profile-section-title">선호 거리</h2>
        <ChipGroup
          idPrefix={`${formIdPrefix}-dist`}
          options={DISTANCES}
          selected={form.preferredDistances}
          onToggle={(v) =>
            setForm((p) => ({ ...p, preferredDistances: toggleIn(p.preferredDistances, v) }))
          }
        />
      </section>

      <section className="profile-section" aria-labelledby={`${formIdPrefix}-region`}>
        <h2 id={`${formIdPrefix}-region`} className="profile-section-title">선호 지역</h2>
        <ChipGroup
          idPrefix={`${formIdPrefix}-region`}
          options={regions.map((r) => ({ value: r.name, label: r.label, idSuffix: r.code }))}
          selected={form.preferredRegions}
          onToggle={(v) =>
            setForm((p) => ({ ...p, preferredRegions: toggleIn(p.preferredRegions, v) }))
          }
        />
      </section>

      <section className="profile-section" aria-labelledby={`${formIdPrefix}-bio`}>
        <h2 id={`${formIdPrefix}-bio`} className="profile-section-title">한줄 소개</h2>
        <div className="profile-field">
          <label className="profile-visually-hidden" htmlFor={`${formIdPrefix}-bio-input`}>
            자기 소개
          </label>
          <textarea
            id={`${formIdPrefix}-bio-input`}
            className="profile-textarea"
            value={form.bio}
            maxLength={BIO_MAX}
            onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
            rows={4}
            placeholder="러닝 스타일, 목표, 훈련 루틴 등을 자유롭게 적어 주세요."
          />
          <div className="profile-field-meta">
            <span className={`profile-char-count ${form.bio.length >= BIO_MAX ? 'is-limit' : ''}`}>
              {form.bio.length} / {BIO_MAX}
            </span>
          </div>
        </div>
      </section>

      <div className="profile-footer-actions">
        <SaveButton
          saving={saving}
          hasChanges={hasChanges}
          hasErrors={hasPaceErrors}
          onClick={handleSave}
          testId="save-bottom"
        />
        <button
          type="button"
          className="profile-btn profile-btn-link"
          onClick={() => setForm(profileToForm(profile))}
          disabled={!hasChanges || saving}
        >
          변경 취소
        </button>
      </div>
    </div>
  );
}

interface SaveButtonProps {
  saving: boolean;
  hasChanges: boolean;
  hasErrors: boolean;
  onClick: () => void;
  testId: string;
}

function SaveButton({ saving, hasChanges, hasErrors, onClick, testId }: SaveButtonProps) {
  const canSave = hasChanges && !hasErrors && !saving;
  const label = saving ? '저장 중…' : hasChanges ? '저장' : '변경사항 없음';
  return (
    <button
      type="button"
      className="profile-btn profile-btn-primary"
      onClick={onClick}
      disabled={!canSave}
      aria-busy={saving}
      aria-disabled={!canSave}
      data-testid={testId}
      title={hasErrors ? '입력값을 확인해주세요' : undefined}
    >
      {saving && <span className="profile-btn-spinner" aria-hidden="true" />}
      <span>{label}</span>
    </button>
  );
}

interface ChipOption<T extends string> {
  value: T;
  label: string;
  /** Id suffix when `value` isn't id-safe (e.g. regions use the numeric code). */
  idSuffix?: string;
}

function ChipGroup<T extends string>({
  idPrefix,
  options,
  selected,
  onToggle,
}: {
  idPrefix: string;
  options: readonly ChipOption<T>[];
  selected: readonly T[];
  onToggle: (value: T) => void;
}) {
  return (
    <div className="profile-chip-grid">
      {options.map((opt) => {
        const id = `${idPrefix}-${opt.idSuffix ?? opt.value}`;
        const checked = selected.includes(opt.value);
        return (
          <label
            key={opt.value}
            className={`profile-chip ${checked ? 'is-checked' : ''}`}
            htmlFor={id}
          >
            <input
              id={id}
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}
